import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
    Sheet,
    SheetContent,
    SheetDescription,
    SheetFooter,
    SheetHeader,
    SheetTitle,
} from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import {
    ColumnDef,
    ColumnFiltersState,
    PaginationState,
    SortingState,
    flexRender,
    getCoreRowModel,
    useReactTable,
} from '@tanstack/react-table';
import { ArrowDown, ArrowUp, ArrowUpDown, ListOrdered, Loader2, MoreHorizontal, Plus, Trash2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useShallow } from 'zustand/react/shallow';
import { trpc } from '~/api/trpc';
import { useRoomsTableFilteringStore } from '~/stores/rooms-table-filtering-store';
import Content from '../components/layout/Content';
import RoomOrderDialog from '../components/rooms/RoomOrderDialog';
import { toast } from 'sonner';

type Room = {
    id: number;
    name: string;
    createdAt: string;
    updatedAt: string;
    deletedAt: string | null;
    categoryId: number | null;
    storeId: number | null;
    room_category: { id: number; name: string } | null;
    store: { id: number; name: string } | null;
    equipment: { id: number; name: string }[];
};

const roomColumns: ColumnDef<Room>[] = [
    {
        accessorKey: 'id',
        header: 'ID',
    },
    {
        accessorKey: 'name',
        header: ({ column }) => {
            return (
                <Button
                    variant='ghost'
                    onClick={() => {
                        const currentSort = column.getIsSorted();
                        if (currentSort === 'asc') {
                            column.toggleSorting(true);
                        } else if (currentSort === 'desc') {
                            column.clearSorting();
                        } else {
                            column.toggleSorting(false);
                        }
                    }}
                >
                    Name
                    {column.getIsSorted() === 'asc' ? (
                        <ArrowUp className='ml-2 h-4 w-4' />
                    ) : column.getIsSorted() === 'desc' ? (
                        <ArrowDown className='ml-2 h-4 w-4' />
                    ) : (
                        <ArrowUpDown className='ml-2 h-4 w-4 opacity-50' />
                    )}
                </Button>
            );
        },
        enableSorting: true,
    },
    {
        id: 'category',
        header: 'Kategorie',
        cell: ({ row }) => {
            return <div>{row.original.room_category?.name ?? '-'}</div>;
        },
    },
    {
        id: 'store',
        header: 'Filiale',
        cell: ({ row }) => {
            return <div>{row.original.store?.name ?? 'gelöscht'}</div>;
        },
    },
];

interface RoomFormData {
    name: string;
    categoryId: number | null;
    storeId: number | null;
}

interface RoomSheetProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    room: Room | null;
    onSave: (data: RoomFormData) => void;
    onDelete: () => void;
    isSaving: boolean;
}

const RoomSheet = ({ open, onOpenChange, room, onSave, onDelete, isSaving }: RoomSheetProps) => {
    const [name, setName] = useState('');
    const [categoryId, setCategoryId] = useState<number | null>(null);
    const [storeId, setStoreId] = useState<number | null>(null);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);

    const { data: categories } = trpc.roomCategories.getAllSimple.useQuery();
    const { data: stores } = trpc.stores.getAllSimple.useQuery();

    useEffect(() => {
        if (room) {
            setName(room.name);
            setCategoryId(room.categoryId);
            setStoreId(room.storeId);
        } else {
            setName('');
            setCategoryId(null);
            setStoreId(null);
        }
    }, [room, open]);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (name.trim() && categoryId && storeId) {
            onSave({ name: name.trim(), categoryId, storeId });
        }
    };

    return (
        <>
            <Sheet open={open} onOpenChange={onOpenChange}>
                <SheetContent className='overflow-y-auto'>
                    <form onSubmit={handleSubmit}>
                        <SheetHeader>
                            <SheetTitle>
                                {room ? 'Raum bearbeiten' : 'Neuer Raum'}
                            </SheetTitle>
                            <SheetDescription>
                                {room
                                    ? 'Bearbeiten Sie die Daten des Raums.'
                                    : 'Geben Sie die Daten für den neuen Raum ein.'}
                            </SheetDescription>
                        </SheetHeader>
                        <div className='flex flex-col gap-4 py-4 px-4'>
                            <div className='flex flex-col gap-2'>
                                <Label htmlFor='name'>Name</Label>
                                <Input
                                    id='name'
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    placeholder='Raumname eingeben'
                                    autoFocus
                                />
                            </div>
                            <div className='flex flex-col gap-2'>
                                <Label htmlFor='category'>Kategorie</Label>
                                <Select
                                    value={categoryId?.toString() ?? ''}
                                    onValueChange={(value) => setCategoryId(parseInt(value, 10))}
                                >
                                    <SelectTrigger>
                                        <SelectValue placeholder='Kategorie wählen' />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {categories?.map((cat) => (
                                            <SelectItem key={cat.id} value={cat.id.toString()}>
                                                {cat.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <p className='text-xs text-muted-foreground'>
                                    Die Kategorie des Raumes bestimmt, welche Art von Behandlungen in diesem Raum durchgeführt werden können.
                                </p>
                            </div>
                            <div className='flex flex-col gap-2'>
                                <Label htmlFor='store'>Filiale</Label>
                                <Select
                                    value={storeId?.toString() ?? ''}
                                    onValueChange={(value) => setStoreId(parseInt(value, 10))}
                                >
                                    <SelectTrigger>
                                        <SelectValue placeholder='Filiale wählen' />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {stores?.map((store) => (
                                            <SelectItem key={store.id} value={store.id.toString()}>
                                                {store.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <p className='text-xs text-muted-foreground'>
                                    Die Filiale, in der sich der Raum befindet.
                                </p>
                            </div>
                        </div>
                        <SheetFooter>
                            {room && (
                                <Button
                                    type='button'
                                    variant='destructive'
                                    onClick={() => setShowDeleteDialog(true)}
                                    disabled={isSaving}
                                >
                                    <Trash2 className='h-4 w-4 mr-2' />
                                    Löschen
                                </Button>
                            )}
                            <Button type='submit' disabled={isSaving || !name.trim() || !categoryId || !storeId}>
                                {isSaving && <Loader2 className='mr-2 h-4 w-4 animate-spin' />}
                                {room ? 'Speichern' : 'Erstellen'}
                            </Button>
                        </SheetFooter>
                    </form>
                </SheetContent>
            </Sheet>

            <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Raum löschen?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Möchten Sie den Raum &quot;{room?.name}&quot; wirklich löschen? Diese Aktion
                            kann nicht rückgängig gemacht werden.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={() => {
                                setShowDeleteDialog(false);
                                onDelete();
                            }}
                            className='bg-destructive text-destructive-foreground hover:bg-destructive/90'
                        >
                            Löschen
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
};

const RoomsDataTable = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const isInitialized = useRef<boolean>(false);

    const {
        sorting: storeSorting,
        columnFilters: storeColumnFilters,
        pagination: storePagination,
        setSorting,
        setColumnFilters,
        setPagination,
    } = useRoomsTableFilteringStore(
        useShallow((state) => ({
            sorting: state.sorting,
            setSorting: state.setSorting,
            columnFilters: state.columnFilters,
            setColumnFilters: state.setColumnFilters,
            pagination: state.pagination,
            setPagination: state.setPagination,
        }))
    );

    const [sheetOpen, setSheetOpen] = useState(false);
    const [selectedRoom, setSelectedRoom] = useState<Room | null>(null);
    const [orderDialogOpen, setOrderDialogOpen] = useState(false);

    useEffect(() => {
        if (isInitialized.current) return;
        isInitialized.current = true;

        const newParams = new URLSearchParams(searchParams);
        let needsUrlUpdate = false;

        const urlSortField = searchParams.get('sort');
        const urlSortDir = searchParams.get('dir') || 'asc';

        if (urlSortField) {
            const urlSortingState: SortingState = [{ id: urlSortField, desc: urlSortDir === 'desc' }];
            if (JSON.stringify(urlSortingState) !== JSON.stringify(storeSorting)) {
                setSorting(urlSortingState);
            }
        } else if (storeSorting.length > 0) {
            newParams.set('sort', storeSorting[0].id);
            newParams.set('dir', storeSorting[0].desc ? 'desc' : 'asc');
            needsUrlUpdate = true;
        }

        const urlPage = searchParams.get('page');
        const urlSize = searchParams.get('size');

        if (urlPage !== null || urlSize !== null) {
            const pageIndex = urlPage !== null ? Math.max(0, parseInt(urlPage, 10) || 0) : storePagination.pageIndex;
            const pageSize = urlSize !== null ? Math.max(1, parseInt(urlSize, 10) || 10) : storePagination.pageSize;
            if (pageIndex !== storePagination.pageIndex || pageSize !== storePagination.pageSize) {
                setPagination({ pageIndex, pageSize });
            }
        } else {
            newParams.set('page', String(storePagination.pageIndex));
            newParams.set('size', String(storePagination.pageSize));
            needsUrlUpdate = true;
        }

        const urlFilters: ColumnFiltersState = [];
        for (const [key, value] of searchParams.entries()) {
            if (key.startsWith('filter_')) {
                urlFilters.push({ id: key.replace('filter_', ''), value });
            }
        }
        if (urlFilters.length > 0) {
            setColumnFilters(urlFilters);
        }

        if (needsUrlUpdate) {
            setSearchParams(newParams, { replace: true });
        }
    }, [searchParams, setSearchParams, storeSorting, storePagination, setSorting, setPagination, setColumnFilters]);

    const queryPage = parseInt(searchParams.get('page') || String(storePagination.pageIndex), 10);
    const queryPageSize = parseInt(searchParams.get('size') || String(storePagination.pageSize), 10);
    const queryFilters: Record<string, string | undefined> = {};
    for (const [key, value] of searchParams.entries()) {
        if (key.startsWith('filter_')) {
            queryFilters[key.replace('filter_', '')] = value;
        }
    }

    const utils = trpc.useUtils();

    const {
        data: roomsResponse,
        isLoading,
        isFetching,
    } = trpc.rooms.getAll.useQuery(
        {
            page: queryPage,
            pageSize: queryPageSize,
            filters: queryFilters,
        },
        {
            placeholderData: (prev) => prev,
        }
    );

    const createMutation = trpc.rooms.create.useMutation({
        onSuccess: () => {
            utils.rooms.getAll.invalidate();
            toast.success('Raum erfolgreich erstellt');
            setSheetOpen(false);
        },
        onError: (error) => {
            toast.error('Fehler beim Erstellen des Raums', { description: error.message });
        },
    });

    const updateMutation = trpc.rooms.update.useMutation({
        onSuccess: () => {
            utils.rooms.getAll.invalidate();
            toast.success('Raum erfolgreich aktualisiert');
            setSheetOpen(false);
        },
        onError: (error) => {
            toast.error('Fehler beim Aktualisieren des Raums', { description: error.message });
        },
    });

    const deleteMutation = trpc.rooms.delete.useMutation({
        onSuccess: () => {
            utils.rooms.getAll.invalidate();
            toast.success('Raum erfolgreich gelöscht');
            setSheetOpen(false);
        },
        onError: (error) => {
            toast.error('Fehler beim Löschen des Raums', { description: error.message });
        },
    });

    useEffect(() => {
        if (!isInitialized.current) return;
        const newParams = new URLSearchParams(searchParams);
        let changed = false;

        if (storeSorting.length > 0) {
            if (searchParams.get('sort') !== storeSorting[0].id) {
                newParams.set('sort', storeSorting[0].id);
                changed = true;
            }
            const dir = storeSorting[0].desc ? 'desc' : 'asc';
            if (searchParams.get('dir') !== dir) {
                newParams.set('dir', dir);
                changed = true;
            }
        } else {
            if (searchParams.has('sort')) {
                newParams.delete('sort');
                newParams.delete('dir');
                changed = true;
            }
        }

        if (changed) {
            setSearchParams(newParams, { replace: true });
        }
    }, [storeSorting, searchParams, setSearchParams]);

    useEffect(() => {
        if (!isInitialized.current) return;
        const currentPage = searchParams.get('page');
        const currentSize = searchParams.get('size');
        const storePage = String(storePagination.pageIndex);
        const storeSize = String(storePagination.pageSize);

        if (currentPage !== storePage || currentSize !== storeSize) {
            const newParams = new URLSearchParams(searchParams);
            newParams.set('page', storePage);
            newParams.set('size', storeSize);
            setSearchParams(newParams, { replace: true });
        }
    }, [storePagination, searchParams, setSearchParams]);

    // Sync filter state with URL
    useEffect(() => {
        if (!isInitialized.current) return;
        const newParams = new URLSearchParams(searchParams);
        let filtersChanged = false;
        const urlFiltersMap = new Map<string, string>();
        Array.from(newParams.keys())
            .filter((key) => key.startsWith('filter_'))
            .forEach((key) => urlFiltersMap.set(key.replace('filter_', ''), newParams.get(key)!));
        const storeFiltersMap = new Map<string, string>();
        storeColumnFilters.forEach((f) => storeFiltersMap.set(f.id, String(f.value)));

        urlFiltersMap.forEach((_, id) => {
            if (!storeFiltersMap.has(id)) {
                newParams.delete(`filter_${id}`);
                filtersChanged = true;
            }
        });
        storeFiltersMap.forEach((value, id) => {
            if (urlFiltersMap.get(id) !== value) {
                if (value) {
                    newParams.set(`filter_${id}`, value);
                } else {
                    newParams.delete(`filter_${id}`);
                }
                filtersChanged = true;
            }
        });

        if (filtersChanged) {
            setSearchParams(newParams, { replace: true });
        }
    }, [storeColumnFilters, searchParams, setSearchParams]);

    const table = useReactTable({
        data: (roomsResponse?.data ?? []) as Room[],
        columns: roomColumns,
        pageCount: roomsResponse?.pageCount ?? -1,
        state: {
            sorting: storeSorting,
            columnFilters: storeColumnFilters,
            pagination: storePagination,
        },
        onPaginationChange: setPagination,
        onSortingChange: setSorting,
        onColumnFiltersChange: setColumnFilters,
        manualPagination: true,
        manualSorting: true,
        manualFiltering: true,
        getCoreRowModel: getCoreRowModel(),
    });

    const handleOpenCreate = () => {
        setSelectedRoom(null);
        setSheetOpen(true);
    };

    const handleOpenEdit = (room: Room) => {
        setSelectedRoom(room);
        setSheetOpen(true);
    };

    const handleSave = (data: RoomFormData) => {
        if (selectedRoom) {
            updateMutation.mutate({
                id: selectedRoom.id,
                name: data.name,
                categoryId: data.categoryId!,
                storeId: data.storeId!,
                equipmentIds: [],
            });
        } else {
            createMutation.mutate({
                name: data.name,
                categoryId: data.categoryId!,
                storeId: data.storeId!,
                equipmentIds: [],
            });
        }
    };

    const handleDelete = () => {
        if (selectedRoom) {
            deleteMutation.mutate({ id: selectedRoom.id });
        }
    };

    const isMutating = createMutation.isPending || updateMutation.isPending || deleteMutation.isPending;

    if (isLoading && !roomsResponse) {
        return (
            <div className='bg-white rounded-lg shadow-sm p-6'>
                <div className='flex items-center justify-between py-4'>
                    <Skeleton className='h-10 w-[250px]' />
                    <Skeleton className='h-10 w-[150px]' />
                </div>
                <div className='rounded-md border border-gray-200'>
                    <Table>
                        <TableHeader>
                            <TableRow>
                                {roomColumns.map((_, index) => (
                                    <TableHead key={index}>
                                        <Skeleton className='h-6 w-full' />
                                    </TableHead>
                                ))}
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {Array.from({ length: 5 }).map((_, rowIndex) => (
                                <TableRow key={rowIndex}>
                                    {roomColumns.map((_, colIndex) => (
                                        <TableCell key={colIndex}>
                                            <Skeleton className='h-6 w-full' />
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            </div>
        );
    }

    return (
        <div className='bg-white rounded-lg shadow-sm p-6'>
            <div className='flex items-center justify-between py-4'>
                <div className='flex gap-4 items-center'>
                    <Input
                        placeholder='Nach Name filtern...'
                        value={(table.getColumn('name')?.getFilterValue() as string) ?? ''}
                        onChange={(event) => {
                            table.getColumn('name')?.setFilterValue(event.target.value);
                        }}
                        className='max-w-sm'
                    />
                    {isFetching && (
                        <div className='flex items-center text-sm text-muted-foreground'>
                            <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                            Laden...
                        </div>
                    )}
                </div>
                <div className='flex gap-2'>
                    <Button variant='outline' onClick={() => setOrderDialogOpen(true)}>
                        <ListOrdered className='mr-2 h-4 w-4' />
                        Reihenfolge
                    </Button>
                    <Button onClick={handleOpenCreate}>
                        <Plus className='mr-2 h-4 w-4' />
                        Neuer Raum
                    </Button>
                </div>
            </div>

            <div className='rounded-md border border-gray-200 overflow-hidden'>
                <Table>
                    <TableHeader className='bg-gray-50'>
                        {table.getHeaderGroups().map((headerGroup) => (
                            <TableRow key={headerGroup.id}>
                                {headerGroup.headers.map((header) => (
                                    <TableHead key={header.id} className='font-medium text-gray-700'>
                                        {header.isPlaceholder
                                            ? null
                                            : flexRender(header.column.columnDef.header, header.getContext())}
                                    </TableHead>
                                ))}
                                <TableHead className='w-[50px]' />
                            </TableRow>
                        ))}
                    </TableHeader>
                    <TableBody>
                        {table.getRowModel().rows?.length ? (
                            table.getRowModel().rows.map((row) => (
                                <TableRow
                                    key={row.id}
                                    className='cursor-pointer hover:bg-gray-50 transition-colors'
                                    onClick={() => handleOpenEdit(row.original)}
                                >
                                    {row.getVisibleCells().map((cell) => (
                                        <TableCell key={cell.id}>
                                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                        </TableCell>
                                    ))}
                                    <TableCell>
                                        <DropdownMenu>
                                            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                                                <Button variant='ghost' className='h-8 w-8 p-0'>
                                                    <span className='sr-only'>Menü öffnen</span>
                                                    <MoreHorizontal className='h-4 w-4' />
                                                </Button>
                                            </DropdownMenuTrigger>
                                            <DropdownMenuContent align='end'>
                                                <DropdownMenuItem
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleOpenEdit(row.original);
                                                    }}
                                                >
                                                    Bearbeiten
                                                </DropdownMenuItem>
                                                <DropdownMenuSeparator />
                                                <DropdownMenuItem
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        navigator.clipboard.writeText(String(row.original.id));
                                                        toast.success('ID kopiert');
                                                    }}
                                                >
                                                    ID kopieren
                                                </DropdownMenuItem>
                                            </DropdownMenuContent>
                                        </DropdownMenu>
                                    </TableCell>
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell
                                    colSpan={roomColumns.length + 1}
                                    className='h-24 text-center'
                                >
                                    {isFetching ? (
                                        <div className='flex justify-center items-center'>
                                            <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                                            Daten werden geladen...
                                        </div>
                                    ) : (
                                        'Keine Räume gefunden.'
                                    )}
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </div>

            <div className='flex items-center justify-between space-x-2 py-4 mt-2'>
                <div className='flex items-center space-x-2'>
                    <select
                        value={table.getState().pagination.pageSize}
                        onChange={(e) => {
                            table.setPageSize(Number(e.target.value));
                        }}
                        className='border border-gray-300 rounded px-2 py-1 text-sm'
                        disabled={isFetching}
                    >
                        {[10, 20, 30, 40, 50].map((pageSize) => (
                            <option key={pageSize} value={pageSize}>
                                {pageSize} pro Seite
                            </option>
                        ))}
                    </select>
                    <div className='text-sm text-muted-foreground'>
                        Seite {table.getState().pagination.pageIndex + 1} von{' '}
                        {table.getPageCount() > 0 ? table.getPageCount() : 1}
                    </div>
                    <Button
                        variant='outline'
                        size='sm'
                        onClick={() => table.previousPage()}
                        disabled={!table.getCanPreviousPage() || isFetching}
                    >
                        &lt;
                    </Button>
                    <Button
                        variant='outline'
                        size='sm'
                        onClick={() => table.nextPage()}
                        disabled={!table.getCanNextPage() || isFetching}
                    >
                        &gt;
                    </Button>
                </div>
            </div>

            <RoomSheet
                open={sheetOpen}
                onOpenChange={setSheetOpen}
                room={selectedRoom}
                onSave={handleSave}
                onDelete={handleDelete}
                isSaving={isMutating}
            />

            <RoomOrderDialog open={orderDialogOpen} onOpenChange={setOrderDialogOpen} />
        </div>
    );
};

const Rooms = () => {
    return (
        <Content>
            <div className='container mx-auto py-10'>
                <h1 className='text-2xl font-bold mb-6'>Räume</h1>
                <RoomsDataTable />
            </div>
        </Content>
    );
};

export default Rooms;
